import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './Docs.css';

export default function PresenceSocketIo() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link
          rel="canonical"
          href="https://www.joegalaxy.net/docs/presence-socket-io"
        />
      </Helmet>

      <header className="docs-lite__header">
        <h1>Presence with Socket.IO: Edge Cases and a Reliable Model</h1>
      </header>

      <main className="docs-lite__content">
        <p>
          Socket.IO makes real-time transport easy, but{' '}
          <strong>transport is not presence</strong>.
          <br />
          A connected socket does not guarantee a human is active, and a disconnect does not
          guarantee they are gone.
        </p>
        <p>
          This guide explains the most common failure modes of Socket.IO presence and a
          lifecycle-aware approach that avoids flapping states, false offline, and multi-tab
          conflicts.
        </p>

        <hr />

        <section>
          <h2>Why “socket connected = online” fails</h2>
          <p>
            A Socket.IO <code>connect</code> event only means:
          </p>
          <ul>
            <li>a client successfully established a transport channel</li>
          </ul>
          <p>
            It does <strong>not</strong> mean:
          </p>
          <ul>
            <li>the user is actively using the app</li>
            <li>the tab is in the foreground</li>
            <li>the network is stable</li>
            <li>the session will remain alive</li>
          </ul>
          <p>
            Browsers suspend background tabs.
            <br />
            Mobile operating systems suspend network stacks.
            <br />
            Wi-Fi handoffs can drop packets without clean disconnects.
          </p>
          <p>If your presence model is:</p>
          <ul>
            <li>
              <code>connect</code> → online
            </li>
            <li>
              <code>disconnect</code> → offline
            </li>
          </ul>
          <p>
            your UI will flap between states and users will interpret it as instability.
          </p>
        </section>

        <hr />

        <section>
          <h2>Presence should be derived, not toggled</h2>
          <p>
            A robust presence system is derived from <strong>signals over time</strong>, not from
            single socket events.
          </p>
          <p>The server should treat presence as:</p>
          <ul>
            <li>a <strong>lastSeenAt</strong> timestamp per session</li>
            <li>
              <strong>TTL-based expiration</strong> for offline
            </li>
            <li>
              <strong>grace windows</strong> for network jitter
            </li>
            <li>
              <strong>derived lifecycle states</strong>, not stored flags
            </li>
          </ul>
          <p>Socket.IO is only the transport that carries these signals.</p>
        </section>

        <hr />

        <section>
          <h2>Session-first: the multi-tab problem</h2>
          <p>Socket.IO makes multi-tab presence tricky:</p>
          <ul>
            <li>one user can have multiple tabs open</li>
            <li>each tab maintains its own socket connection</li>
            <li>one disconnect must not mark the entire user offline</li>
          </ul>
          <p>The correct model:</p>
          <ul>
            <li>
              track <strong>sessions</strong>, not users
            </li>
            <li>
              derive user-level presence from the most recent session state
            </li>
          </ul>
          <p>A simple aggregation rule:</p>
          <ul>
            <li>
              <strong>active</strong> beats idle
            </li>
            <li>
              <strong>idle</strong> beats away
            </li>
            <li>
              <strong>away</strong> beats stale
            </li>
            <li>
              <strong>stale</strong> beats offline
            </li>
          </ul>
          <p>This avoids user-level flapping caused by individual tabs.</p>
        </section>

        <hr />

        <section>
          <h2>Edge cases you must handle</h2>

          <h3>1. Disconnect storms and transport fallbacks</h3>
          <p>
            Socket.IO may reconnect aggressively or switch transports.
            <br />
            Temporary disconnects are common.
          </p>
          <p>
            <strong>Do not treat a disconnect as offline immediately.</strong>
            <br />
            Use TTL expiration with a grace window.
          </p>

          <hr />

          <h3>2. Background tabs</h3>
          <p>The socket may still be connected while the human is gone.</p>
          <p>Presence must incorporate:</p>
          <ul>
            <li>
              document visibility (<code>visible</code> vs <code>hidden</code>)
            </li>
            <li>recent interaction timestamps</li>
            <li>longer heartbeat intervals for backgrounded tabs</li>
          </ul>

          <hr />

          <h3>3. Mobile suspension</h3>
          <p>
            Mobile operating systems can freeze networking without a clean close.
          </p>
          <p>
            Server-side TTL expiration is required.
            <br />
            Disconnect events are not reliable.
          </p>

          <hr />

          <h3>4. Network partitions</h3>
          <p>Packets may drop for minutes without a close frame.</p>
          <p>If you mark offline instantly, presence will oscillate between states.</p>

          <hr />

          <h3>5. Reconnect identity</h3>
          <p>
            Reconnects may create a new <code>socket.id</code>.
          </p>
          <p>
            Presence must bind to a <strong>stable session identifier</strong> you control — not
            only <code>socket.id</code>.
          </p>
        </section>

        <hr />

        <section>
          <h2>A reliable pattern (minimal)</h2>

          <h3>Client: heartbeat + lifecycle signals</h3>
          <p>Clients should emit lightweight heartbeats that include lifecycle state:</p>
          <ul>
            <li>active (foreground interaction)</li>
            <li>idle (no recent interaction)</li>
            <li>background (hidden or suspended)</li>
          </ul>

          <hr />

          <h3>Server: record signal and derive state</h3>
          <pre>
            <code>{`// Record heartbeat signal
onHeartbeat(sessionId, clientState, now) {
  store.set(
    sessionId,
    { state: clientState, lastSeenAt: now },
    TTL_MS
  );
}

// Derive lifecycle state at read time
function deriveState(lastSeenAt, now) {
  const age = now - lastSeenAt;

  if (age < 15_000) return 'active';
  if (age < 60_000) return 'idle';
  if (age < 180_000) return 'away';
  if (age < 240_000) return 'stale';

  return 'offline';
}
`}</code>
          </pre>
        </section>

        <hr />

        <p>
          <strong>Key principle</strong>
        </p>
        <blockquote>
          <p>
            <strong>Offline is derived from time, not a disconnect event.</strong>
          </p>
        </blockquote>

        <hr />

        <section>
          <h2>Reduce noise: publish state changes, not heartbeats</h2>
          <p>A common mistake is broadcasting every heartbeat to all clients.</p>
          <p>Instead:</p>
          <ul>
            <li>store heartbeats quietly</li>
            <li>compute derived state</li>
            <li>
              broadcast only on <strong>meaningful state transitions</strong>
            </li>
          </ul>
          <p>
            This preserves real-time responsiveness without flooding the network.
          </p>
        </section>

        <hr />

        <section>
          <h2>Summary</h2>
          <p>
            Socket.IO is a transport.
            <br />
            Presence is a model.
          </p>
          <p>If you:</p>
          <ul>
            <li>treat connect/disconnect as truth</li>
            <li>store user-level online flags</li>
            <li>ignore lifecycle and TTL</li>
          </ul>
          <p>presence will be noisy and unreliable.</p>
          <p>
            A session-first, lifecycle-aware, TTL-derived model produces presence you can trust.
          </p>
        </section>

        <hr />

        <section>
          <h2>Related docs</h2>
          <ul>
            <li>
              <Link to="/docs/index.md">Docs index</Link>
            </li>
            <li>
              <Link to="/docs/presence-online-offline.md">Online vs Offline Presence</Link>
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
            </li>
            <li>
              <Link to="/docs/presence-socket-io.md">Presence with Socket.IO</Link>
            </li>
            <li>
              <Link to="/docs/webrtc-presence.md">WebRTC Presence</Link>
            </li>
          </ul>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/presence-socket-io.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
